import Socket from '@/socket'
import mqttClient from '@/mqtt/mqtt.client'
import store from '@/vuex/store'
import { mapActions } from 'vuex'
import Sentry from '@/services/sentry'

const TOPIC_PREFIX = 'broadcast-action'
const CASTING_SYNC_GETTER = 'castingScreen/canUseCastingSync'
const registry = {}
let subscribed = false
let storeTopicSubscribed = null
let storeWatcherSetup = false

function getTopic(accessCode) {
  return `${TOPIC_PREFIX}/${accessCode}`
}

// The broker closes the socket on any packet over this size, which shows the "Connecting to server" banner.
const MAX_PACKET_BYTES = 1048576
// Fixed header 1 + remaining-length up to 4 + topic-length 2 + packet id 2, rounded up for MQTT 5 properties.
const PACKET_OVERHEAD_BYTES = 16

function utf8ByteLength(text) {
  return new Blob([text]).size
}

function publish(key, args) {
  const accessCode = store.state.presentation?.uniqueAccessCode
  if (!accessCode) return

  const topic = getTopic(accessCode)
  const payload = JSON.stringify({
    socketId: Socket.getSocketId(),
    key,
    args,
  })
  const payloadBytes = utf8ByteLength(payload)
  if (payloadBytes + utf8ByteLength(topic) + PACKET_OVERHEAD_BYTES > MAX_PACKET_BYTES) {
    const error = new Error(`Broadcast action "${key}" dropped: ${payloadBytes} bytes exceeds the MQTT packet limit`)
    Sentry.captureException(error, { extra: { key, topic, payloadBytes } })
    // eslint-disable-next-line no-console
    console.error(error)
    return
  }
  mqttClient.publish(topic, payload, { retain: false })
}

function handleMessage(topic, message) {
  if (!topic.startsWith(TOPIC_PREFIX)) return
  if (!store.getters[CASTING_SYNC_GETTER] || store.state.isScreenCapture) return

  try {
    const parsed = JSON.parse(message.toString())
    const { socketId, key, args } = parsed
    if (socketId === Socket.getSocketId()) return
    const handlers = registry[key]
    if (!handlers) return
    handlers.forEach((fn) => fn(...args))
  } catch {
    // ignore malformed messages
  }
}

function ensureSubscribed() {
  if (subscribed) return
  subscribed = true
  mqttClient.on('message', handleMessage)
}

function ensureStoreTopicSubscribed() {
  if (!store.getters[CASTING_SYNC_GETTER] || store.state.isScreenCapture) return
  const accessCode = store.state.presentation?.uniqueAccessCode
  if (storeTopicSubscribed === accessCode) return
  if (storeTopicSubscribed) {
    mqttClient.unsubscribe(getTopic(storeTopicSubscribed))
    storeTopicSubscribed = null
  }
  if (!accessCode) return
  storeTopicSubscribed = accessCode
  mqttClient.subscribe(getTopic(accessCode))
}

function setupStoreWatcher() {
  if (storeWatcherSetup) return
  storeWatcherSetup = true
  store.watch(
    (state) => state.presentation?.uniqueAccessCode,
    () => ensureStoreTopicSubscribed(),
    { immediate: true },
  )
}

/**
 * Like Vuex mapActions, but broadcasts the action to other screens via MQTT.
 * The receiver dispatches the same action locally.
 *
 * Usage in a component:
 *   methods: {
 *     ...mapBroadcastActions('leaderboard', ['toggleLeaderboardVisibilityAction']),
 *   }
 */
export function mapBroadcastActions(namespace, actions) {
  try {
    const mapped = {}
    actions.forEach((action) => {
      const actionPath = namespace ? `${namespace}/${action}` : action
      const key = `store::${actionPath}`

      ensureSubscribed()
      setupStoreWatcher()

      if (!registry[key]) {
        registry[key] = [(payload) => {
          store.dispatch(actionPath, { ...payload, _isRemote: true })
        }]
      }

      mapped[action] = function dispatchAndBroadcast(payload = {}) {
        if (payload._isRemote) return
        this.$store.dispatch(actionPath, payload)

        if (!this.$store.getters[CASTING_SYNC_GETTER]) return
        publish(key, [payload])
      }
    })
    return mapped
  } catch (error) {
    Sentry.captureException(error)
    return namespace ? mapActions(namespace, actions) : mapActions(actions)
  }
}

/**
 * Fire-only half of mapBroadcastActions: publishes the broadcast-action message
 * for a store action WITHOUT dispatching it locally. Use from a place that has
 * already applied the state change itself (e.g. a Vuex action that commits
 * directly to dodge a circular dispatch) but still needs the casting screen to
 * mirror it. Same wire format as mapBroadcastActions (`store::<path>`, `[payload]`),
 * so the receiver's registry handler dispatches with `_isRemote: true` and commits
 * state only — no loop. No-op when casting-sync is off.
 */
export function broadcastStoreAction(namespace, action, payload = {}) {
  try {
    if (!store.getters[CASTING_SYNC_GETTER]) return
    const actionPath = namespace ? `${namespace}/${action}` : action
    publish(`store::${actionPath}`, [payload])
  } catch (error) {
    Sentry.captureException(error)
  }
}

/**
 * Wraps a component method so it broadcasts to other screens via MQTT.
 * Requires BroadcastMixin on the component.
 *
 * By default only the casting screen replays it. `replayOnEveryScreen` also
 * replays it on every other screen of the deck (the presenter window, backstage),
 * for state those screens must share, such as a slide's build step.
 *
 * Usage in a component:
 *   methods: {
 *     myMethod: broadcastAction(function myMethod(arg) { ... })
 *   }
 */
export function broadcastAction(fn, { replayOnEveryScreen = false } = {}) {
  const wrapper = function broadcastWrapper(...args) {
    const result = fn.apply(this, args)
    try {
      if (!this.$store.getters[CASTING_SYNC_GETTER]) return result

      const broadcastKey = this._broadcastKeyMap?.get(fn)
      if (!broadcastKey) return result

      const key = `${this.$options.name}::${broadcastKey}`
      publish(key, args)
    } catch (error) {
      Sentry.captureException(error)
    }
    return result
  }
  wrapper.__isBroadcast = true
  wrapper.__originalFn = fn
  wrapper.__replayOnEveryScreen = replayOnEveryScreen
  return wrapper
}

export const BroadcastMixin = {
  inject: {
    isCastingView: { default: false },
  },
  methods: {
    $broadcastOnly(methodName, ...args) {
      if (!this.$store.getters[CASTING_SYNC_GETTER]) return
      const key = `${this.$options.name}::${methodName}`
      publish(key, args)
    },
  },
  created() {
    if (!this.$store.getters[CASTING_SYNC_GETTER]) return

    ensureSubscribed()
    setupStoreWatcher()

    this._broadcastBindings = []
    this._broadcastKeyMap = new Map()
    const methods = this.$options.methods || {}
    Object.keys(methods).forEach((name) => {
      const raw = methods[name]
      if (!raw || !raw.__isBroadcast) return

      // Only the real casting screen should replay, unless the method opts into every screen;
      // other mounted instances can still publish.
      this._broadcastKeyMap.set(raw.__originalFn, name)
      if (!this.isCastingView && !raw.__replayOnEveryScreen) return

      const key = `${this.$options.name}::${name}`
      if (!registry[key]) registry[key] = []
      const bound = raw.__originalFn.bind(this)
      registry[key].push(bound)
      this._broadcastBindings.push({ key, bound })
    })
  },

  beforeDestroy() {
    if (!this._broadcastBindings) return
    this._broadcastBindings.forEach(({ key, bound }) => {
      registry[key] = registry[key].filter((fn) => fn !== bound)
      if (registry[key].length === 0) {
        delete registry[key]
      }
    })
    this._broadcastBindings = null
  },
}
